import { usePreference } from "@/context/preference-context";
import {
    formatDateForUser,
    getTimezoneDisplayName,
    createDateFormatter,
    formatDateInTimezone,
} from "@/utils/dateTimeUtils";

/**
 * Custom hook to access timezone utilities with user's preferences
 * @returns Object with timezone utilities and user's timezone data
 */
export const useTimezone = () => {
    const {
        timezone,
        timeZoneId,
        timeZoneName,
        timeZoneAbbrevName,
        timeZoneValue,
    } = usePreference();

    // Create a formatter with user's timezone
    const formatDate = createDateFormatter(timeZoneValue, timeZoneAbbrevName);

    // Get display name with abbreviation
    const timezoneDisplay = getTimezoneDisplayName(
        timeZoneName,
        timeZoneAbbrevName
    );

    // Format a date with user's timezone and abbreviation
    const formatWithAbbrev = (date: Date | string | null | undefined) => {
        return formatDateForUser(date, timeZoneValue, timeZoneAbbrevName);
    };

    // Format without abbreviation
    const formatWithoutAbbrev = (
        date: Date | string | null | undefined,
        formatStr: string = "MM/dd/yyyy hh:mm a"
    ) => {
        if (!date || !timeZoneValue) return "";
        return formatDateInTimezone(date, timeZoneValue, formatStr);
    };

    return {
        // Complete timezone object
        timezone,

        // Individual timezone fields (for backward compatibility)
        timeZoneId,
        timeZoneName,
        timeZoneAbbrevName,
        timeZoneValue,

        // Display string (e.g., "Eastern Standard Time (EST)")
        timezoneDisplay,

        // Formatting functions
        formatDate, // General formatter
        formatWithAbbrev, // Always includes abbreviation
        formatWithoutAbbrev, // Never includes abbreviation

        // Check if timezone is set
        hasTimezone: !!timeZoneValue,
    };
};

/**
 * Example usage:
 *
 * const MyComponent = () => {
 *     const { timezone, formatWithAbbrev, timezoneDisplay } = useTimezone();
 *
 *     // Access complete timezone object
 *     logger.info('Full timezone:', timezone);
 *     // { timeZoneId: 1, timeZoneName: "Eastern Standard Time", timeZoneAbbrevName: "EST", timeZoneValue: "America/New_York" }
 *
 *     return (
 *         <div>
 *             <p>Your timezone: {timezoneDisplay}</p>
 *             <p>Current time: {formatWithAbbrev(new Date())}</p>
 *             <p>Timezone ID: {timezone.timeZoneId}</p>
 *         </div>
 *     );
 * };
 */
